import type { FeedItem } from '@knocklabs/client';
import { RiAlertLine, RiArrowRightLine, RiCheckLine, RiErrorWarningLine } from '@remixicon/react';
import { NotificationType } from '@shared/types';
import { useNavigate } from '@tanstack/react-router';
import ReferenceCodeChip from 'src/components/atoms/ReferenceCodeChip';
import { appLinks } from 'src/routing/appLinks';

import { relativeTime } from './notificationCell.helpers';

/**
 * Loosely-typed view of AppErrorPayload (Knock feed data is untyped). Mirrors
 * the shared `AppErrorPayload` shape; every field is optional and read
 * defensively. See shared/src/types/notifications.types.ts.
 */
interface AppErrorData {
  type?: string;
  kind?: string;
  tier?: string;
  code?: string;
  error?: string;
  suggestion?: string;
  reference_slug?: string;
  trace_id?: string;
  record_type?: string;
  record_id?: string | number;
  record_slug?: string;
  thread_id?: number;
  link?: string;
}

/** True when this notification is a generic AppError async surface (TAS-3246). */
export function isAppErrorNotification(data: { type?: string } | null): boolean {
  return data?.type === NotificationType.APP_ERROR;
}

type Tone = 'error' | 'warning';

/**
 * Tone by error kind (TAS-3383): system + integration failures read as errors
 * (red); user-correctable kinds (validation / conflict / auth / not_found) read
 * as warnings (orange). Unknown kinds fall to warning — the less alarming tone.
 */
function toneForKind(kind: string | undefined): Tone {
  return kind === 'system' || kind === 'integration' ? 'error' : 'warning';
}

const TONE_STYLES: Record<Tone, { disc: string; icon: string; accent: string; Icon: typeof RiErrorWarningLine }> = {
  error: {
    disc: 'border-red-border bg-red-bg',
    icon: 'text-red-icon',
    accent: 'text-red-text',
    Icon: RiErrorWarningLine,
  },
  warning: {
    disc: 'border-orange-border bg-orange-bg',
    icon: 'text-orange-icon',
    accent: 'text-orange-text',
    Icon: RiAlertLine,
  },
};

/** Resolved in-app deep-link target for an AppError, keyed off `record_type`. */
interface DeepLinkTarget {
  label: string;
  go: () => void;
}

type AppNavigate = ReturnType<typeof useNavigate>;

/**
 * Maps `record_type` → the in-app deep-link the cell navigates to on click.
 * Slug-routed records (bill / sales_order / purchase_order) link off
 * `record_slug` — the backend resolves the numeric `record_id` → slug at notify
 * time, since those routes are keyed on the slug (a raw id would 404). No slug
 * → no CTA. `ai_thread` routes through the AI Inbox thread deep-linker off
 * `thread_id` / `record_id`. Returns null when there's no routable record so
 * the click is a safe no-op.
 */
function resolveDeepLink(data: AppErrorData, navigate: AppNavigate): DeepLinkTarget | null {
  const { record_type, record_id, record_slug, thread_id } = data;

  switch (record_type) {
    case 'bill':
      return record_slug == null
        ? null
        : { label: 'View bill', go: () => void navigate(appLinks.bill({ slug: record_slug })) };
    case 'sales_order':
      return record_slug == null
        ? null
        : { label: 'View order', go: () => void navigate(appLinks.salesOrder({ slug: record_slug })) };
    case 'purchase_order':
      return record_slug == null
        ? null
        : { label: 'View order', go: () => void navigate(appLinks.purchaseOrder({ slug: record_slug })) };
    case 'ai_thread': {
      const threadId = thread_id ?? (record_id == null ? null : Number(record_id));
      return threadId == null || Number.isNaN(threadId)
        ? null
        : { label: 'View thread', go: () => void navigate(appLinks.aiInboxThread({ threadId: String(threadId) })) };
    }
    default:
      // Even without a record_type, an originating thread is a useful landing.
      return thread_id == null
        ? null
        : { label: 'View thread', go: () => void navigate(appLinks.aiInboxThread({ threadId: String(thread_id) })) };
  }
}

interface AppErrorNotificationCellProps {
  item: FeedItem;
  onMarkAsRead?: () => void;
}

export default function AppErrorNotificationCell({ item, onMarkAsRead }: AppErrorNotificationCellProps) {
  const navigate = useNavigate();
  const data = (item.data ?? {}) as AppErrorData;
  const isRead = !!item.read_at;
  const tone = TONE_STYLES[toneForKind(data.kind)];
  const target = resolveDeepLink(data, navigate);

  const handleClick = () => {
    target?.go();
  };

  const headline = data.error ?? 'Something went wrong.';

  return (
    <div
      onClick={handleClick}
      className={`group relative flex flex-row items-start px-5 py-4 gap-3 hover:bg-neutral-2 ${
        target ? 'cursor-pointer' : ''
      }`}
    >
      {/* Unread dot */}
      {!isRead && <span className="absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-green-600" />}

      {/* Icon badge — tone by error kind */}
      <div className={`flex items-center justify-center w-8 h-8 rounded-full border shrink-0 ${tone.disc}`}>
        <tone.Icon size={16} className={tone.icon} />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        {/* Row 1: sanitized error message (the headline) */}
        <span className="text-14 font-medium leading-5 tracking-[-0.1px] text-gray-900 line-clamp-2">{headline}</span>

        {/* Row 2: suggestion (what to do next), when present */}
        {data.suggestion && (
          <div className="flex items-start gap-1">
            <span className={`text-12 leading-4 font-medium ${tone.accent} line-clamp-2`}>{data.suggestion}</span>
          </div>
        )}

        {/* Row 3: reference code as a click-to-copy chip (TAS-3433) */}
        {data.reference_slug && <ReferenceCodeChip code={data.reference_slug} />}
      </div>

      {/* Right column: time + actions */}
      <div className="flex flex-col items-end justify-between gap-2 shrink-0">
        <span className="text-12 leading-4 text-neutral-alpha-9 text-right">{relativeTime(item.inserted_at)}</span>
        <div className="flex items-center gap-1">
          {!isRead && onMarkAsRead && (
            <button
              title="Mark as read"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead();
              }}
              className="hidden group-hover:flex items-center justify-center w-6 h-6 rounded-lg hover:bg-black/8 text-neutral-alpha-9 hover:text-gray-900"
            >
              <RiCheckLine size={14} />
            </button>
          )}
          {target && (
            <button className="flex items-center justify-center px-2 h-6 gap-1 rounded-lg bg-black/4">
              <span className="text-12 font-medium text-gray-900">{target.label}</span>
              <RiArrowRightLine size={14} className="text-neutral-alpha-9" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
